import { useState } from "react";
import { DateRangePicker } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { formatColumnLabel } from "../../../../../../helper/columnLabel";
import {
  formatClockFilterBound,
  toCalendarDate,
  usesCalendarDayFilterParams,
} from "../../../../../../helper/dateTime";

/**
 * Builds the query-param patch and the keys that should be cleared later for a
 * date-range filter header.
 *
 * Shared list layouts pass ISO datetimes by default; a few headers use plain
 * YYYY-MM-DD (or renamed keys). Returning `paramKeys` lets ListView clear only
 * this filter without wiping sibling filters.
 *
 * @param {string} id Field id used in query params (e.g. date_of_creation).
 * @param {string} title Column header title (uppercase in the table).
 * @param {Date} startDate Selected range start.
 * @param {Date} endDate Selected range end.
 * @return {{paramUpdates: Object<string, string>, paramKeys: string[], keysToRemove: string[]}}
 */
export const buildDateFilterParamPatch = (id, title, startDate, endDate) => {
  const endDay = toCalendarDate(endDate);
  const startDay = toCalendarDate(startDate);
  const endClock = formatClockFilterBound(endDate, { endOfDay: true });
  const startClock = formatClockFilterBound(startDate);
  /** @type {Object<string, string>} */
  const paramUpdates = {};
  /** @type {string[]} */
  const keysToRemove = [];

  if (usesCalendarDayFilterParams(id, title)) {
    paramUpdates[`${id}_end_date`] = endDay;
    paramUpdates[`${id}_start_date`] = startDay;
    return {
      paramUpdates,
      paramKeys: Object.keys(paramUpdates),
      keysToRemove,
    };
  }

  paramUpdates[`${id}_end_date`] = endClock;
  paramUpdates[`${id}_start_date`] = startClock;

  if (
    [
      "FROM",
      "TO",
      "BILL DATE",
      "DUE DATE",
      "CREDIT DATE",
      "DATE PAID",
      "VALIDITY DATE",
      "EXPIRY DATE",
      "PLANNED START",
      "REQUIRED BY",
      "EST COMPLETION",
    ].includes(title)
  ) {
    if (["VALIDITY DATE", "EXPIRY DATE"].includes(title)) {
      paramUpdates.validity_date_end = endDay;
      paramUpdates.validity_date_start = startDay;
      keysToRemove.push(`${id}_end_date`, `${id}_start_date`);
      delete paramUpdates[`${id}_end_date`];
      delete paramUpdates[`${id}_start_date`];
    } else {
      paramUpdates[`${id}_end_date`] = endDay;
      paramUpdates[`${id}_start_date`] = startDay;
    }

    if (title === "PICK DATE") {
      paramUpdates.pick_date_end = endDay;
      paramUpdates.pick_date_start = startDay;
      keysToRemove.push(`${id}_end_date`, `${id}_start_date`);
      delete paramUpdates[`${id}_end_date`];
      delete paramUpdates[`${id}_start_date`];
    }
  } else if (title === "DATE") {
    paramUpdates.end_date_date = endDay;
    paramUpdates.start_date_date = startDay;
  } else if (
    ["ORDER DATE", "REQUESTED SHIP DATE"].includes(title) &&
    id !== "date_of_creation"
  ) {
    paramUpdates[`${id}_end`] = endDay;
    paramUpdates[`${id}_start`] = startDay;
    keysToRemove.push(`${id}_end_date`, `${id}_start_date`);
    delete paramUpdates[`${id}_end_date`];
    delete paramUpdates[`${id}_start_date`];
  }

  return {
    paramUpdates,
    paramKeys: Object.keys(paramUpdates),
    keysToRemove,
  };
};

/**
 * Default query keys that may belong to a date-column filter when `paramKeys`
 * were not stored (legacy apply path / remount).
 *
 * @param {string} headerName Lowercase column header.
 * @param {string} [id] Optional field id override from the date filter header.
 * @return {string[]}
 */
export const getFallbackDateFilterParamKeys = (headerName, id) => {
  const fieldId =
    id ||
    (headerName === "create date" || headerName === "date of transaction"
      ? "date_of_creation"
      : headerName === "req date"
        ? "eta"
        : headerName?.replace(/\s+/g, "_"));

  return [
    `${fieldId}_end_date`,
    `${fieldId}_start_date`,
    `${fieldId}_end`,
    `${fieldId}_start`,
    "validity_date_end",
    "validity_date_start",
    "order_date_end",
    "order_date_start",
    "requested_ship_date_end",
    "requested_ship_date_start",
    "pick_date_end",
    "pick_date_start",
    "end_date_date",
    "start_date_date",
  ];
};

export const DateTimeFilterHeaderV2 = ({
  id,
  title,
  setConsignmentsParamsLocal,
  setIsAppliedFilter,
  positionForFilter,
}) => {
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());

  const selectionRanges = {
    endDate: endDate,
    key: "selection",
    startDate: startDate,
  };

  const handleRange = ranges => {
    setStartDate(ranges.selection.startDate);
    setEndDate(ranges.selection.endDate);
  };

  const applyFilter = event => {
    event.preventDefault();
    const { paramUpdates, paramKeys, keysToRemove } = buildDateFilterParamPatch(
      id,
      title,
      startDate,
      endDate,
    );

    setConsignmentsParamsLocal(state => {
      const next = { ...state, ...paramUpdates, offset: 0 };
      keysToRemove.forEach(key => {
        delete next[key];
      });
      return next;
    });
    setIsAppliedFilter(state => {
      return {
        ...state,
        [title.toLowerCase()]: {
          isOpen: false,
          isApplied: true,
          paramKeys,
        },
      };
    });
  };

  const resetFilter = () => {
    const keys = getFallbackDateFilterParamKeys(title.toLowerCase(), id);

    setConsignmentsParamsLocal(state => {
      const next = { ...state, offset: 0 };
      keys.forEach(key => {
        delete next[key];
      });
      return next;
    });
    setIsAppliedFilter(state => {
      return {
        ...state,
        [title.toLowerCase()]: {
          isOpen: false,
          isApplied: false,
          paramKeys: [],
        },
      };
    });
  };

  return (
    <>
      <div
        className="menu menu-sub menu-sub-dropdown text-start show showDateFilter"
        style={positionForFilter}
      >
        <form onSubmit={applyFilter}>
          <div className="px-7 py-5 fs-5 text-gray-900 fw-medium">
            Filter <span className="fw-bold">{formatColumnLabel(title)}</span>
          </div>
          <div className="separator border-gray-200"></div>
          <div className="px-7 py-5">
            <label className="form-label fs-6 fw-semibold">Select Date Range</label>
            <div data-select2-id="select2-data-130-f73w">
              <DateRangePicker ranges={[selectionRanges]} onChange={handleRange} />
            </div>
            <div className="d-flex gap-3 justify-content-end btn-for-date-filter">
              <button
                className="btn btn-light btn-active-light-primary fw-semibold reset-btn"
                data-kt-menu-dismiss="true"
                type="reset"
                onClick={resetFilter}
              >
                Reset
              </button>
              <button
                className="btn btn-primary fw-semibold"
                data-kt-menu-dismiss="true"
                type="submit"
              >
                Apply
              </button>
            </div>
          </div>
        </form>
      </div>
    </>
  );
};
